import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Download, Plus, History, Hammer, Mail, CheckCircle2, StickyNote } from "lucide-react";
import Eye from "lucide-react/dist/esm/icons/eye";
import { toast } from "sonner";
import { gbp, ukDate, ukDateTime } from "../../lib/format";
import { formatApiError } from "../../lib/errors";
import { SLOT_LABEL, downloadQuoteFile, fmtBytes, latestRev } from "../../lib/subby";
import { QuoteComparePanel } from "./QuoteComparePanel";

function Pill({ children, tone = "neutral", testid }) {
  const tones = {
    neutral: { background: "#F0EEE6", color: "#7A6238", border: "1px solid #E2DDD2" },
    amber:   { background: "#FEF3E2", color: "#7A4C0A", border: "1px solid #F2DFB8" },
    red:     { background: "#FBECEC", color: "#7A1F1F", border: "1px solid #E9C0C0" },
    green:   { background: "#E8EFE9", color: "#2F4F3E", border: "1px solid #D6E2D8" },
  };
  return <span className="inline-flex items-center gap-1 rounded font-mono" style={{ ...tones[tone], padding: "1px 6px", fontSize: 9, fontWeight: 700, letterSpacing: "0.06em" }} data-testid={testid}>{children}</span>;
}

function FileSlot({ tenderId, quote, slot, onAddRevision, onView }) {
  const [open, setOpen] = useState(false);
  const revs = quote.files?.[slot] || [];
  const latest = latestRev(quote, slot);
  const dl = async (r) => {
    try { await downloadQuoteFile(tenderId, quote.id, slot, r.rev, r.filename); }
    catch (e) { toast.error(formatApiError(e, "Download failed")); }
  };
  return (
    <div className="rounded-md border border-line bg-surface-2/40 px-3 py-2.5" data-testid={`subby-quote-${quote.id}-slot-${slot}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="col-head" style={{ fontSize: 9.5 }}>{SLOT_LABEL[slot]}{latest ? ` · r${latest.rev}` : ""}</div>
          {latest ? (
            <button type="button" onClick={() => onView(slot, latest)} title="View in a popup" className="mt-0.5 inline-flex items-center gap-1.5 text-ink hover:underline truncate" style={{ fontSize: 12.5, maxWidth: 260 }}
              data-testid={`subby-quote-${quote.id}-view-${slot}`}>
              <Eye size={12} className="text-ink-3 flex-shrink-0" /><span className="truncate">{latest.filename}</span>
            </button>
          ) : (
            <div className="mt-0.5 text-ink-4" style={{ fontSize: 12 }} data-testid={`subby-quote-${quote.id}-empty-${slot}`}>Not uploaded yet</div>
          )}
          {latest && <div className="text-ink-3" style={{ fontSize: 10.5 }}>{fmtBytes(latest.size_bytes)} · {ukDate(latest.uploaded_at)}{latest.uploaded_by_name ? ` · ${latest.uploaded_by_name}` : ""}</div>}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {latest && (
            <button type="button" onClick={() => dl(latest)} title="Download" className="inline-flex items-center justify-center h-7 w-7 rounded text-ink-3 hover:text-ink hover:bg-surface-2" data-testid={`subby-quote-${quote.id}-download-${slot}`}>
              <Download size={12} />
            </button>
          )}
          {revs.length > 1 && (
            <button type="button" onClick={() => setOpen((x) => !x)} className="inline-flex items-center gap-1 h-7 px-2 rounded text-ink-3 hover:text-ink hover:bg-surface-2" style={{ fontSize: 11 }}
              data-testid={`subby-quote-${quote.id}-history-${slot}`}>
              <History size={11} /> {revs.length} revisions
            </button>
          )}
          {quote.status === "active" && (
            <button type="button" onClick={() => onAddRevision(slot)} className="inline-flex items-center gap-1 h-7 px-2 rounded text-ink-2 hover:text-ink hover:bg-surface-2 border border-line" style={{ fontSize: 11 }}
              data-testid={`subby-quote-${quote.id}-add-${slot}`}>
              <Plus size={11} /> {latest ? "Revision" : "Upload"}
            </button>
          )}
        </div>
      </div>
      {open && (
        <ul className="mt-2 border-t border-line-soft pt-2 space-y-1" data-testid={`subby-quote-${quote.id}-revisions-${slot}`}>
          {[...revs].reverse().map((r) => (
            <li key={r.rev} className="flex items-center justify-between gap-2" style={{ fontSize: 11.5 }}>
              <span className="flex items-center gap-1.5 min-w-0">
                <button type="button" onClick={() => onView(slot, r)} className="inline-flex items-center gap-1.5 text-ink-2 hover:underline truncate" data-testid={`subby-quote-${quote.id}-view-${slot}-r${r.rev}`}>
                  <span className="font-mono text-ink-3">r{r.rev}</span><span className="truncate">{r.filename}</span>
                </button>
                <button type="button" onClick={() => dl(r)} className="text-ink-3 hover:text-ink" title="Download" data-testid={`subby-quote-${quote.id}-download-${slot}-r${r.rev}`}><Download size={11} /></button>
              </span>
              <span className="text-ink-3 flex-shrink-0">{r.note ? `${r.note} · ` : ""}{ukDateTime(r.uploaded_at)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** LT-37 — one quote on the tender's Subby Quotes tab. LT-39 — accepted state, details / notes, in-app PDF view. */
export function SubbyQuoteCard({ tenderId, quote, onAddRevision, onVoid, onChanged, onView, onDetails, onAccept, onUnaccept }) {
  const isVoid = quote.status === "void";
  const [compareOpen, setCompareOpen] = useState((quote.compare_line_count || 0) > 0);
  const view = (slot, rev) => onView(quote, slot, rev);
  return (
    <div className={["rounded-lg border bg-surface p-4", isVoid ? "opacity-70 border-line" : quote.accepted ? "border-accent shadow-soft" : "border-line"].join(" ")}
      style={quote.accepted && !isVoid ? { boxShadow: "inset 3px 0 0 #2F4F3E" } : undefined} data-testid={`subby-quote-${quote.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Hammer size={14} className="text-ink-3" />
            <span className="text-ink" style={{ fontSize: 14, fontWeight: 600 }} data-testid={`subby-quote-${quote.id}-name`}>{quote.subcontractor_name}</span>
            {quote.trade_name && <span className="rounded-full px-2 py-0.5 bg-surface-2 text-ink-2 border border-line" style={{ fontSize: 11 }} data-testid={`subby-quote-${quote.id}-trade`}>{quote.trade_name}</span>}
            {quote.accepted && !isVoid && <Pill tone="green" testid={`subby-quote-${quote.id}-accepted-pill`}><CheckCircle2 size={10} /> ACCEPTED</Pill>}
            {isVoid && <Pill tone="red" testid={`subby-quote-${quote.id}-void-pill`}>VOID</Pill>}
            {quote.received_after_issue && <Pill tone="amber" testid={`subby-quote-${quote.id}-after-issue`}>RECEIVED AFTER ISSUE</Pill>}
          </div>
          <div className="text-ink-3 mt-1" style={{ fontSize: 11.5 }}>
            {quote.reference ? `Ref ${quote.reference} · ` : ""}
            {quote.quote_date ? `Dated ${ukDate(quote.quote_date)}` : "No quote date"}
            {quote.valid_until ? ` · valid until ${ukDate(quote.valid_until)}` : ""}
            {` · uploaded ${ukDate(quote.created_at)}${quote.created_by_name ? ` by ${quote.created_by_name}` : ""}`}
            {quote.accepted && !isVoid && <span data-testid={`subby-quote-${quote.id}-accepted-when`}> · accepted {ukDate(quote.accepted_at)}{quote.accepted_by_name ? ` by ${quote.accepted_by_name}` : ""}</span>}
          </div>
          {quote.notes && <div className="text-ink-2 mt-1.5" style={{ fontSize: 12 }}>{quote.notes}</div>}
          {isVoid && <div className="mt-1.5" style={{ fontSize: 12, color: "#7A1F1F" }} data-testid={`subby-quote-${quote.id}-void-reason`}>Voided {ukDate(quote.voided_at)}: {quote.void_reason}</div>}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="col-head" style={{ fontSize: 9.5 }}>Quote (ex VAT)</div>
          <div className={["font-mono tnum", isVoid ? "line-through text-ink-3" : "text-ink"].join(" ")} style={{ fontSize: 18, fontWeight: 600 }} data-testid={`subby-quote-${quote.id}-amount`}>{gbp(quote.amount)}</div>
          {!isVoid && (
            <div className="mt-1.5 flex items-center justify-end gap-2">
              {quote.accepted ? (
                <button type="button" onClick={() => onUnaccept(quote)} className="text-ink-3 hover:text-ink underline" style={{ fontSize: 11 }} data-testid={`subby-quote-${quote.id}-unaccept`}>Un-accept</button>
              ) : (
                <button type="button" onClick={() => onAccept(quote)} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md border border-accent text-accent hover:bg-accent hover:text-white" style={{ fontSize: 11.5, fontWeight: 500 }} data-testid={`subby-quote-${quote.id}-accept`}>
                  <CheckCircle2 size={12} /> Accept
                </button>
              )}
              <button type="button" onClick={() => onVoid(quote)} className="text-ink-3 hover:text-note-red underline" style={{ fontSize: 11 }} data-testid={`subby-quote-${quote.id}-void`}>Void</button>
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap" style={{ fontSize: 11.5 }}>
        <button type="button" onClick={() => onDetails(quote)} className="inline-flex items-center gap-1 text-accent hover:text-accent-deep hover:underline" data-testid={`subby-quote-${quote.id}-details`}>
          <StickyNote size={11} /> Details &amp; notes{quote.note_count ? ` (${quote.note_count})` : ""}
        </button>
        <button type="button" onClick={() => setCompareOpen((x) => !x)} className="inline-flex items-center gap-1 text-accent hover:text-accent-deep hover:underline" data-testid={`subby-quote-${quote.id}-compare-toggle`}>
          {compareOpen ? "Hide comparison" : (quote.compare_line_count ? `Comparison (${quote.compare_line_count} lines)` : "Compare with tender lines")}
        </button>
        <Link to={`?tab=email&subcontractor=${quote.subcontractor_id}`} className="inline-flex items-center gap-1 text-ink-3 hover:text-ink hover:underline" data-testid={`subby-quote-${quote.id}-email`}>
          <Mail size={11} /> Email {quote.subcontractor_name}
        </Link>
      </div>
      {compareOpen && <QuoteComparePanel tenderId={tenderId} quote={quote} onChanged={onChanged} />}
      <div className="grid grid-cols-2 gap-3 mt-3">
        <FileSlot tenderId={tenderId} quote={quote} slot="original" onAddRevision={(s) => onAddRevision(quote, s)} onView={view} />
        <FileSlot tenderId={tenderId} quote={quote} slot="client" onAddRevision={(s) => onAddRevision(quote, s)} onView={view} />
      </div>
    </div>
  );
}
